/**
 * Yappa Platform Search — client behaviour for the shared
 * yappa-platform-search.liquid snippet.
 *
 * Responsibilities:
 *   1. Custom picker interactions — open/close, click-outside,
 *      Escape, option selection.
 *   2. Sync selection across every picker instance on the page
 *      (header + hero + search-page — so the user's platform stays
 *      picked when they navigate between surfaces).
 *   3. Restore the user's last-picked platform from cookie (falls
 *      back to localStorage) on every render.
 *   4. Persist to cookie + localStorage on every change.
 *   5. On form submit, disable the empty hidden input so
 *      `filter.p.m.yappa.console_name=` doesn't end up in the URL
 *      when "All Platforms" is selected (Shopify treats an empty
 *      filter param as "match nothing" on some routes).
 *
 * Cookie: `yappa_platform`, 30-day expiry, SameSite=Lax, Secure.
 * LocalStorage: `yappa-console-filter` (matches the old inline JS's
 * key so existing users' selections survive the migration).
 */
(function () {
  'use strict';

  var COOKIE = 'yappa_platform';
  var LS_KEY = 'yappa-console-filter';
  var COOKIE_MAX_AGE = 60 * 60 * 24 * 30; // 30 days
  var ANIMATION_MS = 250;

  /* ────────── Persistence helpers ────────── */

  function readCookie(name) {
    var m = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
    return m ? decodeURIComponent(m[2]) : '';
  }

  function writeCookie(name, value) {
    var v = value == null ? '' : String(value);
    document.cookie =
      name + '=' + encodeURIComponent(v) +
      '; Max-Age=' + (v === '' ? 0 : COOKIE_MAX_AGE) +
      '; Path=/; SameSite=Lax' +
      (location.protocol === 'https:' ? '; Secure' : '');
  }

  function readPersisted() {
    var fromCookie = readCookie(COOKIE);
    if (fromCookie) return fromCookie;
    try {
      var fromLS = localStorage.getItem(LS_KEY);
      if (fromLS) return fromLS;
    } catch (_) { /* localStorage may be disabled */ }
    return '';
  }

  function writePersisted(value) {
    writeCookie(COOKIE, value);
    try { localStorage.setItem(LS_KEY, value || ''); } catch (_) {}
  }

  /* ────────── Picker attach + behaviour ────────── */

  function findOption(panel, value) {
    // Use manual iteration — CSS.escape is fine but avoids edge cases
    // with unusual values in the future.
    var opts = panel.querySelectorAll('[data-yappa-plat-option]');
    for (var i = 0; i < opts.length; i++) {
      if (opts[i].getAttribute('data-value') === value) return opts[i];
    }
    return null;
  }

  function applySelection(picker, value, display) {
    var valueInput = picker.querySelector('[data-yappa-plat-value]');
    var label = picker.querySelector('[data-yappa-plat-label]');
    var panel = picker.querySelector('[data-yappa-plat-panel]');
    var nativeSelect = picker.querySelector('[data-yappa-plat-native]');
    if (!valueInput || !label || !panel) return;
    valueInput.value = value;
    label.textContent = display;
    var opts = panel.querySelectorAll('[data-yappa-plat-option]');
    for (var i = 0; i < opts.length; i++) {
      var isMatch = opts[i].getAttribute('data-value') === value;
      opts[i].classList.toggle('is-selected', isMatch);
      opts[i].setAttribute('aria-selected', isMatch ? 'true' : 'false');
    }
    // Keep the mobile native select in sync — critical for peer syncs
    // and persistence restore, otherwise the native picker would open
    // pointing at the wrong option.
    if (nativeSelect && nativeSelect.value !== value) {
      nativeSelect.value = value;
    }
  }

  // After a platform pick, drop the caret into the SAME form's search
  // input — saves the customer a click when they're mid-flow.
  // preventScroll: true stops the page from jumping to the input, which
  // is jarring on the hero (input is already in view anyway).
  function focusPairedInput(picker) {
    var form = picker.closest('form[data-yappa-plat-search]');
    if (!form) return;
    var input = form.querySelector('[data-yappa-plat-input]');
    if (input) input.focus({ preventScroll: true });
  }

  function syncPeers(sourcePicker, value, display) {
    var pickers = document.querySelectorAll('[data-yappa-plat-picker]');
    for (var i = 0; i < pickers.length; i++) {
      if (pickers[i] === sourcePicker) continue;
      // Only apply if this peer actually has that option — a peer's
      // nav might render a subset (unlikely but defensive).
      var opt = findOption(pickers[i], value);
      if (value === '' || opt) applySelection(pickers[i], value, display);
    }
  }

  function attachPicker(picker) {
    var trigger = picker.querySelector('[data-yappa-plat-trigger]');
    var panel = picker.querySelector('[data-yappa-plat-panel]');
    var valueInput = picker.querySelector('[data-yappa-plat-value]');
    var label = picker.querySelector('[data-yappa-plat-label]');
    var nativeSelect = picker.querySelector('[data-yappa-plat-native]');
    if (!trigger || !panel || !valueInput || !label) return;

    // Native mobile select — CSS overlays it on top of the trigger at
    // mobile widths so taps hit it first and open the OS-native picker
    // sheet. When the user picks an option, mirror that selection into
    // the hidden filter input + persist + sync peers, same flow as the
    // desktop custom picker.
    if (nativeSelect) {
      nativeSelect.addEventListener('change', function () {
        var value = nativeSelect.value;
        var display = nativeSelect.options[nativeSelect.selectedIndex]
          ? nativeSelect.options[nativeSelect.selectedIndex].textContent
          : value;
        applySelection(picker, value, display);
        writePersisted(value);
        syncPeers(picker, value, display);
        focusPairedInput(picker);
      });
    }

    // Seed from persisted value if not already set server-side. Only
    // apply if the option exists in this picker's list — silently
    // ignore if the persisted value was removed from the nav since
    // last selection.
    if (!valueInput.value) {
      var persisted = readPersisted();
      if (persisted) {
        var opt = findOption(panel, persisted);
        if (opt) applySelection(picker, persisted, opt.getAttribute('data-display'));
      }
    }

    var openTimer = null;
    var closeTimer = null;

    function open() {
      if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
      panel.hidden = false;
      // rAF forces a layout tick so the max-height transition triggers.
      if (openTimer) cancelAnimationFrame(openTimer);
      openTimer = requestAnimationFrame(function () {
        picker.classList.add('is-open');
      });
      trigger.setAttribute('aria-expanded', 'true');
    }

    function close() {
      picker.classList.remove('is-open');
      trigger.setAttribute('aria-expanded', 'false');
      if (closeTimer) clearTimeout(closeTimer);
      closeTimer = setTimeout(function () {
        // Guard — user may have reopened before the animation ended.
        if (!picker.classList.contains('is-open')) panel.hidden = true;
      }, ANIMATION_MS);
    }

    trigger.addEventListener('click', function (e) {
      e.stopPropagation();
      picker.classList.contains('is-open') ? close() : open();
    });

    panel.addEventListener('click', function (e) {
      var opt = e.target.closest('[data-yappa-plat-option]');
      if (!opt) return;
      var value = opt.getAttribute('data-value');
      var display = opt.getAttribute('data-display');
      applySelection(picker, value, display);
      writePersisted(value);
      syncPeers(picker, value, display);
      close();
      focusPairedInput(picker);
    });

    // Click outside closes.
    document.addEventListener('click', function (e) {
      if (!picker.contains(e.target) && picker.classList.contains('is-open')) close();
    });

    // Escape closes + returns focus to trigger.
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && picker.classList.contains('is-open')) {
        close();
        trigger.focus();
      }
    });
  }

  /* ────────── Form-level attach ────────── */

  function attachForm(form) {
    var picker = form.querySelector('[data-yappa-plat-picker]');
    if (picker) attachPicker(picker);

    // On submit, drop an empty platform param so the URL stays clean.
    // Disabled inputs are excluded from FormData / native GET submit.
    form.addEventListener('submit', function () {
      var input = form.querySelector('[data-yappa-plat-value]');
      if (input && !input.value) input.disabled = true;
    });
  }

  function init() {
    document
      .querySelectorAll('form[data-yappa-plat-search]')
      .forEach(attachForm);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
